import {
  OnboardingStatus,
  completeOnboarding,
  completeOnboardingStep,
  resumeOnboarding,
  skipOnboarding
} from './onboarding.js';
import {
  buildProductPreferenceSelection,
  buildSmokingGoalSelection,
  normaliseExperienceSelection
} from './onboarding-fields.js';

export const SMOKEHOUSE_ONBOARDING_STEPS=Object.freeze([
  'experience',
  'goals',
  'products',
  'smoker'
]);

function requireStore(store){
  if(
    !store?.getProfile
    ||!store?.saveProfile
    ||!store?.getOnboarding
    ||!store?.saveOnboarding
  )throw new TypeError('Smokehouse offline store is required');
}

export function createSmokehouseOnboardingFlow({
  store,
  analytics=null,
  now=()=>new Date().toISOString()
}={}){
  requireStore(store);

  function track(method,...args){
    try{analytics?.[method]?.(...args);}catch{}
  }

  async function progress(){
    return store.getOnboarding();
  }

  async function profile(){
    return store.getProfile();
  }

  async function resume(){
    const current=await progress();
    const next=resumeOnboarding(current,{steps:SMOKEHOUSE_ONBOARDING_STEPS,now:now()});
    if(next!==current)await store.saveOnboarding(next);
    if(current.status===OnboardingStatus.NOT_STARTED)track('onboardingStarted',next.currentStep);
    return next;
  }

  async function advance(step,nextStep){
    const current=await progress();
    if(current.status===OnboardingStatus.COMPLETED)return current;

    const active=current.status===OnboardingStatus.IN_PROGRESS
      ?current
      :resumeOnboarding(current,{steps:SMOKEHOUSE_ONBOARDING_STEPS,now:now()});

    const next=completeOnboardingStep(active,step,{nextStep});
    return store.saveOnboarding(next);
  }

  async function saveExperience(experienceLevel,{advanceOnboarding=true}={}){
    const value=normaliseExperienceSelection(experienceLevel);
    const saved=await store.saveProfile({experienceLevel:value});
    if(advanceOnboarding)await advance('experience','goals');
    return saved;
  }

  async function saveGoals(smokingGoals,{otherText=null,advanceOnboarding=true}={}){
    const value=buildSmokingGoalSelection(smokingGoals,{otherText});
    const saved=await store.saveProfile({
      smokingGoals:value.values,
      customSmokingGoal:value.otherText
    });
    if(advanceOnboarding)await advance('goals','products');
    return saved;
  }

  async function saveProducts(productPreferences,{otherText=null,advanceOnboarding=true}={}){
    const value=buildProductPreferenceSelection(productPreferences,{otherText});
    const saved=await store.saveProfile({
      productPreferences:value.values,
      customProductPreference:value.otherText
    });
    if(advanceOnboarding)await advance('products','smoker');
    return saved;
  }

  async function completeAfterSmoker(){
    const current=await progress();
    const active=current.status===OnboardingStatus.IN_PROGRESS
      ?current
      :resumeOnboarding(current,{steps:SMOKEHOUSE_ONBOARDING_STEPS,now:now()});
    const withSmoker=completeOnboardingStep(active,'smoker',{nextStep:null});
    const completed=completeOnboarding(withSmoker,{now:now()});
    const saved=await store.saveOnboarding(completed);
    track('onboardingCompleted');
    return saved;
  }

  async function skip(){
    const current=await progress();
    const next=skipOnboarding(current,{now:now()});
    const saved=await store.saveOnboarding(next);
    track('onboardingSkipped',current.currentStep||'unknown');
    return saved;
  }

  async function state(){
    return Object.freeze({
      profile:await profile(),
      progress:await progress()
    });
  }

  return Object.freeze({
    resume,
    state,
    saveExperience,
    saveGoals,
    saveProducts,
    completeAfterSmoker,
    skip,
    editExperience:value=>saveExperience(value,{advanceOnboarding:false}),
    editGoals:(values,options={})=>saveGoals(values,{...options,advanceOnboarding:false}),
    editProducts:(values,options={})=>saveProducts(values,{...options,advanceOnboarding:false})
  });
}
